import {styled} from '@linaria/react';
import React, {useMemo} from 'react';
import {Text, TextProps} from './Text';

/**
 * A text component that automatically scales font size based on text length
 *
 * How it works:
 * - Shorter text gets bigger font size
 * - Longer text gets smaller font size
 * - Formula: fontSize = maxFontSize * Math.max(minScaleRatio, 1 - textLength / scalingSensitivity)
 *
 * Examples with maxFontSize=32 and scalingSensitivity=20:
 * - 1 character:    scaleRatio = 1 - (1/20) = 0.95  → fontSize = 32 * 0.95 = 30px
 * - 5 characters:   scaleRatio = 1 - (5/20) = 0.75  → fontSize = 32 * 0.75 = 24px
 * - 10 characters:  scaleRatio = 1 - (10/20) = 0.5  → fontSize = 32 * 0.5 = 16px
 * - 15 characters:  scaleRatio = 1 - (15/20) = 0.25 → fontSize = 32 * 0.5 = 16px (clamped to minimum)
 * - 20+ characters: scaleRatio = 1 - (20/20) = 0    → fontSize = 32 * 0.5 = 16px (clamped to minimum)
 *
 * @param $textLength - Number of characters in the text
 * @param $maxFontSize - Maximum font size (in pixels) for very short text
 * @param $scalingSensitivity - Controls scaling sensitivity (higher = more gradual scaling)
 */
export const ScalableText: React.FC<
  TextProps & {
    text: string;
    maxFontSize: number;
    scalingSensitivity?: number;
  }
> = ({text, maxFontSize, scalingSensitivity = 20, ...rest}) => {
  const scaledFontSize = useMemo(() => {
    const minScaleRatio = 0.5;
    const scaleRatio = Math.max(
      minScaleRatio,
      1 - text.length / scalingSensitivity,
    );

    return `${Math.round(maxFontSize * scaleRatio)}px`;
  }, [text, maxFontSize, scalingSensitivity]);

  return (
    <StyledText $scaledFontSize={scaledFontSize} {...rest}>
      {text}
    </StyledText>
  );
};

const StyledText = styled(Text)<{
  $scaledFontSize: string;
}>`
  white-space: nowrap;
  font-size: ${props => props.$scaledFontSize};
`;
